import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fileSection", "textSection", "inputTypeField", "fileInput", "textInput", "submitButton", "filePlaceholder"]

  connect() {
    this.validateForm()
    this.element.setAttribute("data-ready", "")
    // Listen for paste events on the form element
    this.element.addEventListener("paste", this.handlePaste.bind(this))
  }

  disconnect() {
    this.element.removeEventListener("paste", this.handlePaste.bind(this))
  }

  async handlePaste(event) {
    // Don't interfere if user is pasting text into the text input
    if (event.target === this.textInputTarget || this.textInputTarget.contains(event.target)) {
      return
    }

    const clipboardData = event.clipboardData
    if (!clipboardData) return

    // Check if clipboard contains image data
    const items = Array.from(clipboardData.items)
    const imageItem = items.find(item => item.type.startsWith("image/"))

    if (!imageItem) return

    // Prevent default paste behavior
    event.preventDefault()

    // Get the image blob/file from clipboard
    const clipboardFile = imageItem.getAsFile()
    if (!clipboardFile || clipboardFile.size === 0) {
      console.warn("Clipboard file is empty or invalid")
      return
    }

    // Read the blob data to ensure it's fully available
    // This ensures the file data is complete before creating the File object
    const arrayBuffer = await clipboardFile.arrayBuffer()
    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      console.error("Failed to read clipboard file data")
      return
    }

    // Create a File object with proper name and metadata from the array buffer
    const extension = clipboardFile.type.split("/")[1] || "png"
    const fileName = `pasted-image-${Date.now()}.${extension}`

    const file = new File([arrayBuffer], fileName, {
      type: clipboardFile.type,
      lastModified: clipboardFile.lastModified || Date.now()
    })

    // Verify the file was created correctly
    if (!file || file.size === 0) {
      console.error("Failed to create valid file from clipboard")
      return
    }

    // Ensure file section is visible
    if (this.fileSectionTarget.classList.contains("hidden")) {
      this.showFileInput()
    }

    // Create a DataTransfer object to set the file on the input
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)

    // Set the files on the file input
    this.fileInputTarget.files = dataTransfer.files

    // Set input type to file
    this.inputTypeFieldTarget.value = "file"

    // Dispatch change event so form properly recognizes the file
    const changeEvent = new Event("change", { bubbles: true })
    this.fileInputTarget.dispatchEvent(changeEvent)

    // Trigger validation
    this.validateForm()
  }

  showTextInput() {
    this.fileSectionTarget.classList.add("hidden")
    this.textSectionTarget.classList.remove("hidden")
    this.inputTypeFieldTarget.value = "text"
    this.fileInputTarget.value = ""
    // Show placeholder when clearing file input
    if (this.hasFilePlaceholderTarget) {
      this.filePlaceholderTarget.classList.remove("hidden")
    }
    this.validateForm()
  }

  showFileInput() {
    this.textSectionTarget.classList.add("hidden")
    this.fileSectionTarget.classList.remove("hidden")
    this.inputTypeFieldTarget.value = "file"
    this.textInputTarget.value = ""
    this.validateForm()
  }

  validateForm() {
    const inputType = this.inputTypeFieldTarget.value
    const hasFile = this.fileInputTarget.files && this.fileInputTarget.files.length > 0
    const hasText = this.textInputTarget.value.trim().length > 0

    const isValid = (inputType === "file" && hasFile) || (inputType === "text" && hasText)

    // Show/hide file placeholder and toggle text visibility
    if (this.hasFilePlaceholderTarget) {
      if (hasFile) {
        this.filePlaceholderTarget.classList.add("hidden")
        // Show filename text when file is selected
        this.fileInputTarget.classList.remove("text-transparent")
        this.fileInputTarget.classList.add("text-white")
      } else {
        this.filePlaceholderTarget.classList.remove("hidden")
        // Hide "No file chosen" text when no file
        this.fileInputTarget.classList.add("text-transparent")
        this.fileInputTarget.classList.remove("text-white")
      }
    }

    this.submitButtonTarget.disabled = !isValid

    if (isValid) {
      this.submitButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
      this.submitButtonTarget.classList.add("cursor-pointer")
    } else {
      this.submitButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
      this.submitButtonTarget.classList.remove("cursor-pointer")
    }
  }
}

